/**
 * LotowheelModule — admin del juego LotoWheel (sorteos lotéricos de la fortuna).
 *
 * Estructura del archivo:
 *
 *   PARTE A — CONSTANTES TOP-LEVEL (líneas ~23-213):
 *     - THEMES, BET_GROUPS, TIPOS_DE_RUEDA_PARA_UI_ADMIN (matemática derivada).
 *     - DEFAULT_CHIPS, paleta de colores fichas.
 *     - SSOT: la disponibilidad de apuestas por tipo de rueda se DERIVA
 *       matemáticamente (no se hardcodea), idéntico a
 *       shared/config/roulette-numbers-order-config.js.
 *
 *   PARTE B — MODULO LotowheelModule (línea ~217+):
 *     1. ESTADO         memoria mutable del módulo (declarar aquí, no ad-hoc).
 *     2. INIT           render inicial (theme grid).
 *     3. CRUD LISTA     loadLotowheelConfigs() + render cards.
 *     4. MODAL PRINCIPAL  openLotowheelConfigModal/save/delete — patrón canónico
 *                         (Borrador + CONTRATO_SORTEO_LOTOWHEEL + bloqueo banking).
 *     5. RESOLVER       loader + collect del config del algoritmo.
 *     6. PRESETS        clonar/listar presets de marketing del resolver.
 *     7. CHIPS          builder visual + collect de fichas.
 *     8. APUESTAS       toggle de grupos habilitados.
 *     9. ZERO DISPLAY   modo de presentación del cero.
 *    10. SERIES TICKETS overrides por sorteo (superadmin).
 *
 * Patrón canónico aplicado en MODAL PRINCIPAL:
 *   - openModal hidrata DOM desde snapshot del backend (?hidratar=true).
 *   - save() crea Borrador, escribirCampoEnPath por input, valida contra
 *     CONTRATO_SORTEO_LOTOWHEEL, manda copia completa al backend.
 *   - Backend deepMerge preserva campos no expuestos (ej. limites.maxApuesta).
 *   - Bloqueo banking del botón Guardar durante hidratación (race-safe).
 */

import { App, Api } from '../../../admin.js';
import { icon } from '../../../utils/icon.js';
import { skeletonCardGrid } from '../../../components/skeleton-loader.js';
import { MonedaService } from '../../../services/moneda-service.js';
import { t } from '../../../services/i18n-admin.js';
import {
    DEFAULT_TIMING,
    DEFAULT_SPIN,
    DEFAULT_EFFECTS,
    DEFAULT_CONFIG,
    DEFAULT_LIMITES,
    DEFAULT_FICHAS,
} from '@shared/config/games/lotowheel/defaults.js';
import { ROL_ADMIN } from '@shared/config/business-constants.js';
import {
    SCHEDULER_APLICACION,
    SCHEDULER_CODIGO_ERROR,
    SCHEDULER_CONFIRMAR_DESTRUCCION,
    TICKETS_MAX_CONFIRM_DIALOG,
} from '@shared/config/scheduler-aplicacion.js';
// SSOT real de los tipos de rueda. front-admin NO duplica las fórmulas de
// multiplicadores ni de disponibilidad de apuestas — las DERIVA de la config
// que el SSOT ya construyó. Único punto de verdad: si el backend cambia un
// multiplicador, el admin lo refleja sin tocar este archivo.
import { ROULETTE_TYPES as TIPOS_DE_RUEDA_SSOT } from '@shared/config/games/lotowheel/roulette-numbers-order-config.js';
import {
    CANAL_DE_VENTA_TICKET_AL_PORTADOR,
    CANAL_DE_VENTA_WALLET_DE_JUGADOR_IDENTIFICADO,
    resolverApuestasHabilitadasParaCanalDeVenta,
    normalizarApuestasHabilitadasParaPersistencia,
} from '@shared/config/games/lotowheel/canal-de-venta.js';
import { escapeHtmlParaInyeccionSegura } from '../../../utils/escape-html-para-inyeccion-segura.js';
import { BorradorDeEdicionDeEntidadConSnapshotOriginal } from '@shared/contracts/_helpers/borrador-de-edicion-de-entidad-con-snapshot-original.js';
import { CONTRATO_SORTEO_LOTOWHEEL } from '@shared/contracts/sorteo-lotowheel.js';
import { motivoErrorAHumano } from '@shared/contracts/_helpers/validar-payload-contra-contrato.js';

// Tipo inicial que aparece pre-seleccionado en el form de admin al CREAR un
// sorteo nuevo. Es UX, no money-path: derivado de las recetas (cualquier
// receta agregada/borrada en recetas-agnosticas.js mueve este valor sin
// tocar codigo). Cero magic string 'european' en admin.
export const TIPO_DE_RUEDA_INICIAL_PARA_FORM_ADMIN = Object.keys(TIPOS_DE_RUEDA_SSOT)[0];

// Mapa path → etiqueta humana para errores de validación contra contrato.
// Espejo del patrón de pozos-comunes.js. Cualquier path no listado cae al
// fallback (la key cruda) sin romper el toast.
const ETIQUETAS_HUMANAS_POR_PATH_DEL_SORTEO = {
    nombre: 'nombre',
    intervalo_segundos: 'intervalo entre sorteos',
    activo: 'estado activo',
    tipoRuleta: 'tipo de ruleta',
    tema: 'tema visual',
    sound_pack: 'pack de sonidos',
    idioma: 'idioma',
    close_before: 'segundos cierre apuestas',
    warning_time: 'segundos warning previo',
    min_usable_time: 'segundos útiles mínimos',
    spin_time: 'duración del giro',
    'moneda.codigo': 'código de moneda',
    'moneda.simbolo': 'símbolo de moneda',
    'moneda.decimales': 'decimales de moneda',
    'moneda.separadorDecimal': 'separador decimal',
    'moneda.separadorMiles': 'separador de miles',
    'fichas.denominacion': 'denominación de fichas',
    'fichas.chips': 'chips disponibles',
    'spin.duration': 'duración del giro',
    'spin.phases.accel': 'fase aceleración',
    'spin.phases.cruise': 'fase crucero',
    'spin.phases.decel': 'fase desaceleración',
    'effects.start': 'efecto al iniciar',
    'effects.end': 'efecto al terminar',
    'idle.mode': 'modo idle',
    'idle.timeout': 'timeout idle',
    'pozo.min_mult': 'multiplicador mínimo del pozo',
    'pozo.max_mult': 'multiplicador máximo del pozo',
};

// ═══════════════════════════════════════════════════════════════════════
// PARTE A — CONSTANTES LOTOWHEEL (themes, bet groups, ruedas, fichas).
// Derivaciones matemáticas idénticas a roulette-numbers-order-config.js.
// ═══════════════════════════════════════════════════════════════════════

export const THEMES = [
    { id: 'CASINO_ROYALE', name: 'Casino Royale', colors: ['#1a1a2e', '#c9a227', '#b8860b'] },
    { id: 'HALLOWEEN', name: 'Halloween', colors: ['#1a0a1a', '#ff6600', '#8b0000'] },
    { id: 'CYBERPUNK_MATRIX', name: 'Matrix', colors: ['#0d0d0d', '#00ff41', '#003b00'] },
    { id: 'ROYAL_VENGANZA', name: 'Royal Venganza', colors: ['#1a0a2e', '#9400d3', '#4b0082'] },
    { id: 'DEEP_OCEAN', name: 'Deep Ocean', colors: ['#0a1628', '#0077b6', '#00b4d8'] },
    { id: 'CYBERPUNK_NEON', name: 'Neon', colors: ['#0f0f23', '#ff0080', '#00ffff'] },
    { id: 'MIDNIGHT_SAPPHIRE', name: 'Sapphire', colors: ['#0a0a1a', '#1e3a8a', '#3b82f6'] },
];

// SSOT: presets de moneda viven en MonedaService, no en el juego
export const CURRENCY_PRESETS = MonedaService.PRESETS;

// Mapeo de checkboxes-de-UI a ids agnosticos de la receta. El admin pinta
// un checkbox por entrada — cada uno marca/desmarca los ids declarados.
// Pleno (aciertoDirecto) siempre habilitado (no tiene toggle).
export const BET_GROUPS = {
    betDuo: ['duo'],
    betTrio: ['linea'],
    betCuarteto: ['cuadra'],
    betSexteto: ['dobleLinea'],
    betColor: ['rojos', 'negros'],
    betParidad: ['pares', 'impares'],
    betMitad: ['bajos', 'altos'],
    betTercios: ['docena1', 'docena2', 'docena3'],
    betColumnas: ['columna1', 'columna2', 'columna3'],
    betBloques: ['bloqueA', 'bloqueB', 'bloqueC', 'bloqueD', 'bloqueE', 'bloqueF'],
    betEspejos: ['espejo_12_21', 'espejo_13_31', 'espejo_23_32'],
    betGemelos: ['gemelos'],
};

// ─── Helpers de UI por canal (§8.14) ─────────────────────────────────
//
// Los checkboxes de la pestaña Apuestas son DOS por grupo: `<groupId>_ticket`
// y `<groupId>_wallet`. Estos helpers concentran el patrón "leer/escribir
// el checkbox del canal X" para que ningún caller toque ids con string
// concatenation a mano (zona de typos silenciosos).
//
// FAIL-FAST: aceptan solo los literales canónicos de canal del SSOT
// (`'ticket'` / `'wallet'`). Un typo en el caller produce error explícito
// en vez de IDs inexistentes silenciosos (que harían "todo on" sin que
// nadie lo note). Espejo del fail-fast del resolver de lectura.
const CANALES_DE_VENTA_VALIDOS_EN_UI = new Set([
    CANAL_DE_VENTA_TICKET_AL_PORTADOR,
    CANAL_DE_VENTA_WALLET_DE_JUGADOR_IDENTIFICADO,
]);
function _exigirCanalValido(canal) {
    if (!CANALES_DE_VENTA_VALIDOS_EN_UI.has(canal)) {
        throw new Error(
            `[admin/lotowheel] canal de venta desconocido: "${canal}". ` +
                `Esperado uno de: '${CANAL_DE_VENTA_TICKET_AL_PORTADOR}' | ` +
                `'${CANAL_DE_VENTA_WALLET_DE_JUGADOR_IDENTIFICADO}'.`
        );
    }
}

/**
 * Marca los checkboxes del canal dado según una lista de ids habilitados.
 * Un grupo está habilitado si TODOS sus ids individuales están en la lista.
 * Si `apuestasHabilitadas` es `null`, se marca todo (sin restricción).
 *
 * @param {string[]|null} apuestasHabilitadas
 * @param {'ticket'|'wallet'} canal
 */
function _aplicarCheckboxesDeCanalParaGrupos(apuestasHabilitadas, canal) {
    _exigirCanalValido(canal);
    if (apuestasHabilitadas === null || apuestasHabilitadas === undefined) {
        Object.keys(BET_GROUPS).forEach(groupId => {
            const checkbox = document.getElementById(`${groupId}_${canal}`);
            if (checkbox) checkbox.checked = true;
        });
        return;
    }
    const enabledSet = new Set(apuestasHabilitadas);
    Object.entries(BET_GROUPS).forEach(([groupId, betIds]) => {
        const checkbox = document.getElementById(`${groupId}_${canal}`);
        if (!checkbox) return;
        checkbox.checked = betIds.every(id => enabledSet.has(id));
    });
}

// `pleno` SIEMPRE está habilitada — es la apuesta básica de la rueda
// (apuestar a un número específico). NO tiene checkbox de toggle: no se
// puede deshabilitar. Si la lista de apuestas habilitadas se materializa
// como array (modo "con restricción"), `pleno` debe estar dentro o el
// consumidor (cashier/customer/tv) la trata como deshabilitada → bug
// money-path crítico. Esta constante centraliza el invariante.
const APUESTAS_SIEMPRE_HABILITADAS_SIN_CHECKBOX_UI = ['aciertoDirecto'];

/**
 * Recolecta la lista de ids de apuesta habilitados desde los checkboxes
 * del canal dado. Devuelve `null` si TODOS los grupos están on (= sin
 * restricción para ese canal); sino devuelve el array plano de ids
 * INCLUYENDO `pleno` siempre primero (no tiene checkbox, ver constante
 * arriba — su ausencia en el array material lo deshabilitaría en money-
 * path).
 *
 * El helper SSOT `normalizarApuestasHabilitadasParaPersistencia` decide
 * después cómo persistir (plano vs multi-canal).
 *
 * @param {'ticket'|'wallet'} canal
 * @returns {string[]|null}
 */
function _recolectarApuestasHabilitadasDelDOMParaCanal(canal) {
    _exigirCanalValido(canal);
    const ids = [];
    let todosOn = true;
    Object.entries(BET_GROUPS).forEach(([groupId, betIds]) => {
        const checkbox = document.getElementById(`${groupId}_${canal}`);
        if (checkbox && checkbox.checked) {
            ids.push(...betIds);
        } else {
            todosOn = false;
        }
    });
    if (todosOn) return null;
    // Materializamos la lista: prefijar las apuestas sin-checkbox-UI
    // (`pleno`) para que no queden silenciosamente fuera. Si por error
    // ya estaban en `ids` (paranoia), el dedup del helper SSOT
    // `normalizarApuestasHabilitadasParaPersistencia` las colapsa.
    return [...APUESTAS_SIEMPRE_HABILITADAS_SIN_CHECKBOX_UI, ...ids];
}

// ── Tipos de rueda — PROYECCIÓN del SSOT, sin duplicar lógica ──────────────
//
// front-admin NO mantiene su propia tabla de tipos: derivа cada tipo de la
// config que `roulette-numbers-order-config.js` ya construyó. Solo los textos
// de display puro (nombre comercial, rango legible) viven acá — el SSOT no los
// tiene porque son presentación del admin, no del juego.
//
// Si el backend agrega un tipo o cambia un multiplicador, el admin lo refleja
// sin tocar este archivo. Cero divergencia silenciosa.

// Textos de presentación admin (lo único que el SSOT no provee). El resto de
// cada tipo se deriva de su config en el SSOT.
const ETIQUETAS_DE_TIPO_DE_RUEDA = {
    european: { label: 'Fortuna Clasica', range: '0-36', tercios: '1-12, 13-24, 25-36', mitad: '1-18 / 19-36' },
    american: {
        label: 'Fortuna Americana',
        range: '0, 00, 1-36',
        tercios: '1-12, 13-24, 25-36',
        mitad: '1-18 / 19-36',
    },
    triple_zero: {
        label: 'Fortuna Triple Cero',
        range: '0, 00, 000, 1-36',
        tercios: '1-12, 13-24, 25-36',
        mitad: '1-18 / 19-36',
    },
    mini: { label: 'Mini Fortuna', range: '0-12', tercios: '1-4, 5-8, 9-12', mitad: '1-6 / 7-12' },
    mini18: { label: 'Midi Fortuna', range: '0-18', tercios: '1-6, 7-12, 13-18', mitad: '1-9 / 10-18' },
    rueda10: { label: 'Rueda 10', range: '0-10', tercios: null, mitad: '1-5 / 6-10' },
    rueda10_american: { label: 'Rueda 10 Americana', range: '0, 00, 1-10', tercios: null, mitad: '1-5 / 6-10' },
    // Ruedas lightning (multiplicadores estilo Evolution). Misma geometría que la
    // Fortuna Clásica (37 sectores); las diferencia el bloque `lightning` de la receta.
    mega3000: {
        label: 'Mega Roulette 3000',
        range: '0-36',
        tercios: '1-12, 13-24, 25-36',
        mitad: '1-18 / 19-36',
    },
    reddoor: {
        label: 'Red Door',
        range: '0-36',
        tercios: '1-12, 13-24, 25-36',
        mitad: '1-18 / 19-36',
    },
};

// La config del SSOT está indexada por clave en minúsculas (espejo del id de
// la receta). Normalizamos la caja por si llega un tipoId de un caller viejo.
const _configSsotDeTipo = tipoId => TIPOS_DE_RUEDA_SSOT[String(tipoId).toLowerCase()] || null;

// Proyecta la config del SSOT al shape de display que el admin consume
// (`sectors`, `cols`, `blockCount`, `blockMult` + las etiquetas). Cada campo
// numérico se deriva del SSOT — ninguno se hardcodea.
function _proyectarTipoDeRueda(tipoId) {
    const c = _configSsotDeTipo(tipoId);
    if (!c) return null;
    const etiquetas = ETIQUETAS_DE_TIPO_DE_RUEDA[tipoId] || {};
    const apuestas = Array.isArray(c.apuestas) ? c.apuestas : [];
    const bloques = apuestas.filter(a => a.grupo === 'bloques');
    const columnas = apuestas.filter(a => a.grupo === 'columnas');
    const primerBloque = bloques[0];
    const ultimoBloque = bloques[bloques.length - 1];
    return {
        label: etiquetas.label || tipoId,
        sectors: c.totalNumbers,
        range: etiquetas.range || (c.matGrid ? `0-${c.matGrid.numbers}` : `0-${c.totalNumbers}`),
        blocks: bloques.length ? `${primerBloque.id}-${ultimoBloque.id}` : null,
        blockCount: bloques.length,
        blockMult: primerBloque ? primerBloque.multiplicador : 0,
        cols: columnas.length,
        tercios: etiquetas.tercios,
        mitad: etiquetas.mitad,
    };
}

// Tabla de tipos de rueda para la UI del admin — proyeccion enriquecida con
// etiquetas (label/range/tercios/mitad) sobre la config del SSOT. NO es la
// tabla money-path: para esa, importar ROULETTE_TYPES desde
// roulette-numbers-order-config.js directamente.
//
// La LISTA se deriva del SSOT (no de las etiquetas hardcodeadas): cada rueda con
// `seleccionableEnAdmin` (default true; solo `mundial` está en false hoy) aparece
// acá. Agregar/ocultar una rueda del admin es UN cambio en su receta — se acabó
// tocar 3 sitios del front (ver PENDIENTE-tipoRuleta-dinamico.md). Las etiquetas
// siguen siendo enriquecimiento opcional: sin entrada, `_proyectarTipoDeRueda` cae
// al `tipoId` crudo y al rango derivado del SSOT.
export const TIPOS_DE_RUEDA_PARA_UI_ADMIN = Object.fromEntries(
    Object.keys(TIPOS_DE_RUEDA_SSOT)
        .filter(tipoId => TIPOS_DE_RUEDA_SSOT[tipoId].seleccionableEnAdmin)
        .map(tipoId => [tipoId, _proyectarTipoDeRueda(tipoId)])
);

// Disponibilidad de apuestas — leída de `availableBets` del SSOT (que
// buildConfig ya derivó), más los flags geométricos que el SSOT no nombra
// (bloques/columnas, presentes según `blocks`/`columns` de la config).
export function getBetAvailability(tipoId) {
    const c = _configSsotDeTipo(tipoId);
    if (!c) return null;
    const apuestas = Array.isArray(c.apuestas) ? c.apuestas : [];
    const tieneGrupo = grupo => apuestas.some(a => a.grupo === grupo && a.activa !== false);
    return {
        hasTrio: tieneGrupo('linea'),
        hasSexteto: tieneGrupo('dobleLinea'),
        hasCuarteto: tieneGrupo('cuadra'),
        hasMirrors: tieneGrupo('espejos'),
        hasTwins: tieneGrupo('gemelos'),
        hasTercios: tieneGrupo('tercios'),
        hasParidad: tieneGrupo('paridad'),
        hasBloques: tieneGrupo('bloques'),
        hasColumnas: apuestas.filter(a => a.grupo === 'columnas' && a.activa !== false).length >= 2,
        hasMitad: tieneGrupo('mitad'),
        hasColor: tieneGrupo('color'),
    };
}

// Multiplicadores por GRUPO de apuesta — lee `apuesta.multiplicador` del SSOT.
// Como cada grupo puede tener varias apuestas (color: rojos/negros, ambas x2),
// devuelve el de la primera apuesta del grupo (todas comparten multiplicador
// en grupos "binarios" y multinumero). Una receta con multiplicadores distintos
// por id deberia exponer cada uno por separado — este resumen es para UI.
export const BET_MULTIPLIERS = {
    forType(tipoId) {
        const c = _configSsotDeTipo(tipoId);
        if (!c) return null;
        const apuestas = Array.isArray(c.apuestas) ? c.apuestas : [];
        const multPrimeraDelGrupo = grupo => {
            const a = apuestas.find(x => x.grupo === grupo && x.activa !== false);
            return a ? a.multiplicador : 0;
        };
        const multGeometrica = grupo => {
            const a = apuestas.find(x => x.grupo === grupo && typeof x.aridad === 'number' && x.activa !== false);
            return a ? a.multiplicador : 0;
        };
        return {
            pleno: multGeometrica('directo'),
            duo: multGeometrica('duo'),
            trio: multGeometrica('linea'),
            cuarteto: multGeometrica('cuadra'),
            sexteto: multGeometrica('dobleLinea'),
            bloque: multPrimeraDelGrupo('bloques'),
            tercio: multPrimeraDelGrupo('tercios'),
            columna: multPrimeraDelGrupo('columnas'),
            color: multPrimeraDelGrupo('color'),
            paridad: multPrimeraDelGrupo('paridad'),
            mitad: multPrimeraDelGrupo('mitad'),
            espejo: multPrimeraDelGrupo('espejos'),
            gemelos: multPrimeraDelGrupo('gemelos'),
        };
    },
};

export const DEFAULT_CHIPS = [
    { valor: 1, label: '1', color: '#1976d2' },
    { valor: 5, label: '5', color: '#d32f2f' },
    { valor: 25, label: '25', color: '#388e3c' },
    { valor: 50, label: '50', color: '#7b1fa2' },
    { valor: 100, label: '100', color: '#212121' },
    { valor: 500, label: '500', color: '#ffc107' },
];

// Paleta de colores para fichas de casino (orden clasico)
export const CHIP_COLORS = [
    '#1976d2', // Azul
    '#d32f2f', // Rojo
    '#388e3c', // Verde
    '#7b1fa2', // Purpura
    '#212121', // Negro
    '#ffc107', // Amarillo/Oro
    '#ff5722', // Naranja
    '#00bcd4', // Cyan
    '#e91e63', // Rosa
    '#795548', // Marron
    '#607686', // Gris azulado
    '#009688', // Teal
    '#673ab7', // Violeta
    '#3f51b5', // Indigo
    '#8bc34a', // Lima
    '#ff9800', // Ambar
];

// Progresion estandar de fichas de casino (en creditos)
export const CHIP_CREDIT_VALUES = [1, 2, 5, 10, 20, 50, 100, 200, 500];

// ═══════════════════════════════════════════════════════════════════════
// PARTE B — MÓDULO LotowheelModule (ver mapa completo en header).
// ═══════════════════════════════════════════════════════════════════════

// Para activar la sección "Parámetros Avanzados" del resolver en el modal, cambiar a true.
// También actualiza el display:none del <details id="resolverConfigDetails"> en lotowheel-modals.js.
export const MOSTRAR_PARAMS_AVANZADOS = false;

export const LotowheelModule = {
    // ═══════════════════════════════════════════════════════════════════════
    // ESTADO — toda la memoria mutable del módulo se declara aquí.
    // Regla: nunca crear propiedades ad-hoc dentro de métodos.
    // ═══════════════════════════════════════════════════════════════════════

    // --- Lista CRUD ---
    /** Config del sorteo actualmente abierto en el modal (null = nuevo) */
    currentSorteoConfig: null,

    // --- Tab Fichas ---
    /** Array de fichas en el editor: { valor, label, color } */
    currentChips: [...DEFAULT_CHIPS],

    // --- Tab Resolver ---
    /** Resolvers disponibles cargados del backend */
    _resolverCache: null,
    /** Presets de resolver cargados del backend */
    _resolverPresetsCache: null,
    /** Presets filtrados para el resolver activo en el dropdown */
    _presetsParaResolver: [],
    /** Handler de cierre del dropdown de presets (para deregistrar el listener) */
    _presetDropdownCloseHandler: null,
    /** Nombre del preset activo (para mostrarlo en el card y persistirlo al guardar) */
    _activePresetNombre: null,

    // --- Tab Algoritmo / Sliders ---
    /** Definiciones de los 5 sliders cargadas del backend */
    _sliderDefinitions: null,

    // --- Tab Acceso ---
    /** Lista de nodos operador disponibles para el whitelist */
    _accesoNodos: [],
    /** Set de nodo_ids marcados como autorizados (cambios pendientes hasta Guardar) */
    _accesoNodoIdsSeleccionados: new Set(),

    // --- Tab Pozos Comunes ---
    /** Lista de todos los pozos comunes existentes en el sistema */
    _pozosComunesDisponibles: [],
    /** Suscripciones actuales del sorteo abierto (cargadas desde API una sola vez por sesión) */
    _suscripcionesPozosComunes: [],

    // --- Cache anti-refetch ---
    /**
     * ID del sorteo cuyo estado está en memoria.
     * Si el usuario abre el mismo sorteo y cambia de tab, NO se re-fetchea la API.
     * Se invalida al abrir un sorteo distinto o al cancelar.
     */
    _cachedModalSorteoId: null,

    // ═══════════════════════════════════════════════════════════════════════
    // INIT — render inicial de la theme grid (setup único al boot del admin).
    // ═══════════════════════════════════════════════════════════════════════

    init() {
        this.renderThemeGrid();
    },

    // ═══════════════════════════════════════════════════════════════════════
    // CRUD LISTA — loadLotowheelConfigs() + render cards de sorteos.
    // ═══════════════════════════════════════════════════════════════════════

    async loadLotowheelConfigs() {
        // Estado de carga ANTES del fetch: sin esto la lista tardaba segundos en
        // blanco y parecía un cuelgue. Mensaje específico + shimmer con la forma
        // de las cards reales. Solo si el grid está vacío — en un refresh se
        // mantienen las cards actuales (sin parpadeo).
        const gridCargando = document.getElementById('lotowheelConfigsGrid');
        if (gridCargando && !gridCargando.childElementCount) {
            gridCargando.dataset.cargando = '1';
            gridCargando.innerHTML =
                `<div style="grid-column:1/-1;color:var(--text-secondary);font-size:13px;">` +
                `${t('Consultando sorteos y estado del scheduler…')}</div>` +
                skeletonCardGrid(6, { minWidth: '260px' });
        }

        const configs = await Api.getSorteoConfigs();
        const grid = document.getElementById('lotowheelConfigsGrid');
        if (grid && grid.dataset.cargando) {
            delete grid.dataset.cargando;
            // Fetch fallido con el skeleton puesto: limpiarlo para no dejar un
            // shimmer eterno que vuelva a simular el cuelgue.
            if (!configs) grid.innerHTML = '';
        }
        if (!configs) return;

        // Helpers para etiquetas humanas en el card
        const _ruedaLabel = tipoId => {
            const tipo = TIPOS_DE_RUEDA_PARA_UI_ADMIN[tipoId];
            return tipo ? tipo.label + ' (' + tipo.sectors + ')' : tipoId;
        };
        const _temaLabel = temaId => {
            if (!temaId) return null;
            const t = THEMES.find(th => th.id === temaId);
            return t ? t.name : temaId;
        };
        const _resolverLabel = name => {
            if (!name) return null;
            if (name === 'volatilidad-controlada') return 'Lotería Ponderada';
            if (name === 'random') return 'Aleatorio';
            if (name === 'weighted-rtp') return 'RTP Ponderado';
            return name;
        };

        grid.innerHTML = configs
            .map(c => {
                const r = c.resumen || {};
                const ruedaLabel = _ruedaLabel(r.tipoRuleta || TIPO_DE_RUEDA_INICIAL_PARA_FORM_ADMIN);
                const temaLabel = _temaLabel(r.tema);
                const resolverLabel = _resolverLabel(r.resolverName);
                // Línea 2: resolver + preset (si existe) — o aviso rojo si no hay resolver
                // XSS fix (auditoría 2026-05-30 XSS workflow #2/#3): r.presetNombre y
                // c.nodo_nombre vienen de DB editables por admin/operador → cross-tenant
                // XSS si no se escapan. Helper SSOT del módulo admin.
                const resolverBadge = resolverLabel
                    ? `<span class="config-badge">${escapeHtmlParaInyeccionSegura(resolverLabel)}${r.presetNombre ? ' · ' + escapeHtmlParaInyeccionSegura(r.presetNombre) : ''}</span>`
                    : `<span class="config-badge" style="color:#ef5350;">${t('Sin algoritmo matemático')}</span>`;
                // Badge de propietario: Global (superadmin) o nombre del operador dueño
                const propietarioBadge = c.nodo_id
                    ? `<span class="config-badge" style="color:var(--accent-blue);border-color:var(--accent-blue);">${c.nodo_nombre ? escapeHtmlParaInyeccionSegura(c.nodo_nombre) + ' · #' + c.nodo_id : '#' + c.nodo_id}</span>`
                    : `<span class="config-badge" style="opacity:0.5;">${t('Global')}</span>`;
                // Pozos comunes suscritos — bloque con nombre + valor grande (mismo estilo card pozos)
                const pozos = r.pozos || [];
                let pozosHtml = '';
                for (let pi = 0; pi < pozos.length; pi++) {
                    const p = pozos[pi];
                    const pct = p.umbral > 0 ? Math.min(100, Math.round((p.actual / p.umbral) * 100)) : 0;
                    pozosHtml += `
                        <div class="game-pozo">
                          <div style="display:flex; justify-content:space-between; align-items:baseline; margin-bottom:2px;">
                            <div class="game-pozo-label" style="font-size:13px; font-weight:600; color:var(--text-secondary);">${escapeHtmlParaInyeccionSegura(p.nombre)}</div>
                            <div class="game-pozo-label">${pct}% del umbral</div>
                          </div>
                          <div class="game-pozo-value">${App.formatMoney(p.actual, p.moneda)}</div>
                        </div>`;
                }
                return `
      <div class="game-card" data-action="lotowheel:openConfigModal" data-id="${c.id}" data-nodo-id="${c.nodo_id || ''}">
        <div class="game-card-header">
          <div>
            <div class="game-name">${escapeHtmlParaInyeccionSegura(c.nombre)}</div>
            <div class="game-interval">${t('Intervalo:')} ${c.intervalo_segundos}s${this._renderEstadoSchedulerInline(c)}</div>
          </div>
          <span class="game-status ${c.activo ? 'active' : 'inactive'}">${c.activo ? t('ACTIVO') : t('PAUSADO')}</span>
        </div>
        ${pozosHtml}
        <div class="game-config-badges">
          <div style="display:flex;gap:6px;flex-wrap:wrap;">
            <span class="config-badge">${ruedaLabel}</span>
            ${temaLabel ? `<span class="config-badge">${temaLabel}</span>` : ''}
            ${propietarioBadge}
          </div>
          <div>${resolverBadge}</div>
          <div><span class="config-badge" style="opacity:0.4;">ID: ${c.id}</span></div>
        </div>
      </div>
    `;
            })
            .join('');
        // Iconos locales
    },

    // Renderiza una línea inline al lado del intervalo con el estado real del scheduler
    // según lo que el backend admin nos devuelve (proxy GET /status batch).
    //   - scheduler_activo === true  → "▶ corriendo · próximo en Ns"
    //   - scheduler_activo === false → "■ detenido en RAM"  (resaltado si DB.activo=1)
    //   - scheduler_activo === null  → "estado del scheduler desconocido"
    //   - divergencia_db_ram         → marca roja con "DB y RAM no coinciden"
    _renderEstadoSchedulerInline(c) {
        if (c.scheduler_activo === undefined) return '';
        if (c.scheduler_activo === null) {
            return ` · <span style="color:#ffb74d;" title="No se pudo consultar el scheduler en loto-api">${t('⚠ estado scheduler desconocido')}</span>`;
        }
        const proximo = c.proximo_sorteo_en != null ? ` · ${t('próximo en')} ${c.proximo_sorteo_en}s` : '';
        if (c.scheduler_activo) {
            const divergente = c.divergencia_db_ram
                ? ` <span style="color:#ef5350;" title="DB dice una cosa y el scheduler RAM otra">${t('⚠ divergente')}</span>`
                : '';
            return ` · <span style="color:#66bb6a;">${t('▶ corriendo')}${proximo}</span>${divergente}`;
        }
        const divergente = c.divergencia_db_ram
            ? ` <span style="color:#ef5350;" title="DB activo=true pero scheduler RAM detenido">${t('⚠ divergente DB↔RAM')}</span>`
            : '';
        return ` · <span style="color:#bdbdbd;">${t('■ scheduler detenido')}</span>${divergente}`;
    },

    // ═══════════════════════════════════════════════════════════════════════
    // MODAL PRINCIPAL — patrón canónico (Borrador + contrato + bloqueo banking).
    // openLotowheelConfigModal hidrata, save() persiste con deepMerge backend.
    // ═══════════════════════════════════════════════════════════════════════

    async openLotowheelConfigModal(id = null, nodoIdFromCard = null, opts = {}) {
        // Invalidar cache si se abre un sorteo diferente al que estaba en memoria
        if (this._cachedModalSorteoId !== (id || null)) {
            this._cachedModalSorteoId = id || null;
            this._pozosComunesDisponibles = [];
            this._suscripcionesPozosComunes = [];
            this._accesoNodos = [];
            this._accesoNodoIdsSeleccionados = new Set();
        }
        App.openModal('modalLotowheelConfig');

        // Tab Acceso: visible para superadmin O para el propietario del sorteo.
        // La evaluamos después de cargar la config (más abajo), por ahora ocultar;
        // se revelará en _maybeShowTabAcceso() tras conocer config.nodo_id.

        // Reset to tab (default: primera; opts.tab permite ir a otra)
        const targetTab = opts.tab || null;
        const tabs = document.querySelectorAll('#modalLotowheelConfig .modal-tab:not([style*="display: none"])');
        const panels = document.querySelectorAll('#modalLotowheelConfig .tab-panel');
        let targetIdx = 0;
        if (targetTab) {
            for (let i = 0; i < tabs.length; i++) {
                if (tabs[i].dataset.tab === targetTab) {
                    targetIdx = i;
                    break;
                }
            }
        }
        tabs.forEach((t, i) => t.classList.toggle('active', i === targetIdx));
        panels.forEach((p, i) => p.classList.toggle('active', i === targetIdx));

        document.getElementById('modalLotowheelConfigTitle').textContent = id
            ? t('Editar LotoWheel')
            : t('Nuevo LotoWheel');
        document.getElementById('lotowheelConfigId').value = id || '';

        // Reset estado del módulo que persiste entre aperturas. Con el patrón
        // canónico (Borrador desde snapshot hidratado del backend), el SAVE
        // lee del Borrador y NO del DOM, así que un dropdown contaminado por
        // sesión previa NO se persiste. Pero limpiamos visualmente para que
        // el usuario no vea por 150ms el preset viejo del sorteo anterior.
        this._activePresetNombre = null;

        // Permisos inmediatos (sin esperar fetch) usando nodo_id de la card
        const dangerZone = document.getElementById('dangerZone');
        const btnGuardar = document.getElementById('btnGuardarLotowheel');
        const puedeEditar = !id || App.currentUser.rol === ROL_ADMIN.SUPERADMIN || !!nodoIdFromCard;

        if (dangerZone) dangerZone.style.display = id && puedeEditar ? 'block' : 'none';

        // Bloqueo del botón Guardar durante hidratación. Patrón banking:
        // el modal recién abierto NO acepta guardar hasta que el snapshot
        // del backend esté hidratado en los inputs. Sin esto, los HTML
        // defaults pintados por _panelSpin/etc. (ej. spin.duration=8000)
        // se persistirían si el usuario tapea Guardar en los ~150ms del
        // fetch — pisando los valores reales de DB con basura HTML.
        // Al terminar `openLotowheelConfigModal`, restauramos el estado
        // del botón según permisos (puedeEditar).
        this._modalCargando = true;
        if (btnGuardar) {
            btnGuardar.disabled = true;
            btnGuardar.style.opacity = '0.5';
            btnGuardar.title = t('Cargando configuración del sorteo...');
        }

        // Guardamos puedeEditar para restaurar al final del openModal.
        this._puedeEditarModalActual = puedeEditar;

        if (id) {
            const sorteoConfig = await Api.getSorteoConfig(id);
            if (!sorteoConfig) return;
            this.currentSorteoConfig = sorteoConfig;

            // Snapshot para diff PATCH al guardar (solo se manda lo cambiado).
            this._configSnapshotAlAbrir = sorteoConfig.config ? JSON.parse(JSON.stringify(sorteoConfig.config)) : {};

            // Mostrar/ocultar tab Acceso según rol y propiedad del sorteo
            this._maybeShowTabAcceso(sorteoConfig);

            const c = sorteoConfig.config || {};

            // Guardar ID para branding uploads
            this._editingSorteoConfigId = sorteoConfig.id;

            // General
            document.getElementById('lotowheelConfigNombre').value = sorteoConfig.nombre || '';
            document.getElementById('lotowheelConfigIntervalo').value = sorteoConfig.intervalo_segundos || 60;
            document.getElementById('lotowheelConfigActivo').value = sorteoConfig.activo ? '1' : '0';

            // Tipo de Rueda
            document.getElementById('lotowheelConfigTipoRuleta').value =
                c.tipoRuleta || TIPO_DE_RUEDA_INICIAL_PARA_FORM_ADMIN;
            this.onTipoRuletaChange();

            // Timing
            document.getElementById('lotowheelConfigCloseBefore').value = c.close_before ?? DEFAULT_TIMING.close_before;
            document.getElementById('lotowheelConfigWarningTime').value = c.warning_time ?? DEFAULT_TIMING.warning_time;
            document.getElementById('lotowheelConfigMinUsable').value =
                c.min_usable_time ?? DEFAULT_TIMING.min_usable_time;
            document.getElementById('lotowheelConfigSpinTime').value = c.spin_time ?? DEFAULT_TIMING.spin_time;

            // Visual
            this.selectTheme(c.tema || DEFAULT_CONFIG.tema);
            document.getElementById('lotowheelConfigSoundPack').value = c.sound_pack || DEFAULT_CONFIG.sound_pack;
            document.getElementById('lotowheelConfigIdioma').value = c.idioma || DEFAULT_CONFIG.idioma;

            // Moneda. Para códigos ISO conocidos, simbolo/decimales/separadores
            // SIEMPRE se muestran derivados del preset real — nunca de lo
            // guardado en `mon.*`, que puede ser dato legacy inconsistente
            // (ej. {codigo:'PYG', decimales:2} de antes de existir el preset
            // PYG). Mismo criterio que el backend en _resolverMoneda — evita
            // que el admin vea/reguarde una combinación que ya no es válida.
            const mon = c.moneda || {};
            const monedaDefault = MonedaService.getPreset(MonedaService.ORDER[0]);
            const presetDelCodigoGuardado = mon.codigo ? MonedaService.PRESETS?.[mon.codigo] : null;
            const monResuelta = presetDelCodigoGuardado || mon;
            document.getElementById('lotowheelConfigMonedaCodigo').value = mon.codigo || monedaDefault.codigo;
            document.getElementById('lotowheelConfigMonedaSimbolo').value =
                monResuelta.simbolo || monedaDefault.simbolo;
            document.getElementById('lotowheelConfigMonedaDecimales').value =
                monResuelta.decimales ?? monedaDefault.decimales;
            document.getElementById('lotowheelConfigMonedaSepDec').value =
                monResuelta.separadorDecimal || monedaDefault.separadorDecimal;
            document.getElementById('lotowheelConfigMonedaSepMil').value =
                monResuelta.separadorMiles || monedaDefault.separadorMiles;

            // Fichas
            const fichas = c.fichas || {};
            document.getElementById('lotowheelConfigDenominacion').value =
                fichas.denominacion || DEFAULT_FICHAS.denominacion;
            this.currentChips = fichas.chips?.length ? [...fichas.chips] : [...DEFAULT_CHIPS];
            this.renderChipsList();

            // Limites
            const lim = c.limites || {};
            document.getElementById('lotowheelConfigPremioMax').value =
                lim.premioMaximo || DEFAULT_LIMITES.premioMaximo;
            this.validatePremioMaximo();

            // Vigencia de tickets
            document.getElementById('lotowheelConfigTicketExpiryDays').value = c.ticketExpiryDays || 180;

            // Series de tickets — override por sorteo (superadmin only)
            await this._populateSeriesTicketsDropdowns(sorteoConfig);

            // Spin
            const spin = c.spin || {};
            document.getElementById('lotowheelConfigSpinDuration').value = spin.duration || DEFAULT_SPIN.duration;
            document.getElementById('lotowheelConfigSpinCorrection').value =
                spin.correctionDelay || DEFAULT_SPIN.correctionDelay;
            document.getElementById('lotowheelConfigSpinRPM').value = spin.cruiseRPM || DEFAULT_SPIN.cruiseRPM;
            // tensionFinal y phases.* aceptan 0 como valor válido del contrato
            // (minInclusive: 0). `??` preserva el 0 explícito del admin; `||`
            // lo trataría como nulo y pintaría default — bug.
            document.getElementById('lotowheelConfigSpinTension').value =
                spin.tensionFinal ?? DEFAULT_SPIN.tensionFinal;
            document.getElementById('lotowheelConfigSpinFM').value = spin.fmQuesitos || DEFAULT_SPIN.fmQuesitos;
            const phases = spin.phases || {};
            document.getElementById('lotowheelConfigPhaseAccel').value = phases.accel ?? DEFAULT_SPIN.phases.accel;
            document.getElementById('lotowheelConfigPhaseCruise').value = phases.cruise ?? DEFAULT_SPIN.phases.cruise;
            document.getElementById('lotowheelConfigPhaseDecel').value = phases.decel ?? DEFAULT_SPIN.phases.decel;

            // Effects
            const eff = c.effects || {};
            document.getElementById('lotowheelConfigEffectStart').value = eff.start || DEFAULT_EFFECTS.start;
            document.getElementById('lotowheelConfigEffectEnd').value = eff.end || DEFAULT_EFFECTS.end;

            // Idle
            const idle = c.idle || {};
            document.getElementById('lotowheelConfigIdleMode').value = idle.mode || 'all';
            // idle.timeout acepta 0 (deshabilitar idle); usar `??` no `||`.
            document.getElementById('lotowheelConfigIdleTimeout').value = idle.timeout ?? 10;

            // Presentacion del cero
            this.populateZeroDisplayConfig(c.zero_display_mode, c.zero_display_labels);

            // Branding
            this.populateBrandingPreviews(c.assets, sorteoConfig);

            // Resolver
            await this.loadResolverOptions();
            const resolverCfg = c.resolver || { name: 'random', config: {} };
            document.getElementById('resolverName').value = resolverCfg.name;
            this.onResolverChange();
            // Aplicar config guardada a los campos dinámicos
            if (resolverCfg.config) {
                const cfgKeys = Object.keys(resolverCfg.config);
                for (let ri = 0; ri < cfgKeys.length; ri++) {
                    const input = document.getElementById('resolverCfg_' + cfgKeys[ri]);
                    if (input) {
                        const val = resolverCfg.config[cfgKeys[ri]];
                        if (typeof val === 'object' && val !== null) {
                            input.value = JSON.stringify(val, null, 2);
                        } else {
                            input.value = val;
                        }
                    }
                }
            }
            // Restaurar sliders del perfil de operador si fueron guardados
            if (resolverCfg.sliders) {
                const sliderKeys = Object.keys(resolverCfg.sliders);
                for (let si = 0; si < sliderKeys.length; si++) {
                    const slider = document.getElementById('slider_' + sliderKeys[si]);
                    if (slider) slider.value = resolverCfg.sliders[sliderKeys[si]];
                }
            }

            // Restaurar nombre del preset activo en el buscador
            this._activePresetNombre = resolverCfg.presetNombre || null;
            const presetInput = document.getElementById('presetSearchInput');
            const presetClear = document.getElementById('presetSearchClear');
            if (presetInput) {
                presetInput.value = this._activePresetNombre || '';
                if (presetClear) presetClear.style.display = this._activePresetNombre ? '' : 'none';
            }

            // Apuestas - cargar grupos habilitados POR CANAL (§8.14).
            // El resolver SSOT devuelve la lista efectiva por canal: si el
            // sorteo trae `apuestasHabilitadasPorCanalDeVenta` la usa, sino
            // cae a la forma plana `apuestasHabilitadas` (legacy → mismas
            // listas en ambos canales). null = sin restricción = todo on.
            const apuestasParaCanalTicket = resolverApuestasHabilitadasParaCanalDeVenta(
                c,
                CANAL_DE_VENTA_TICKET_AL_PORTADOR
            );
            const apuestasParaCanalWallet = resolverApuestasHabilitadasParaCanalDeVenta(
                c,
                CANAL_DE_VENTA_WALLET_DE_JUGADOR_IDENTIFICADO
            );
            _aplicarCheckboxesDeCanalParaGrupos(apuestasParaCanalTicket, CANAL_DE_VENTA_TICKET_AL_PORTADOR);
            _aplicarCheckboxesDeCanalParaGrupos(apuestasParaCanalWallet, CANAL_DE_VENTA_WALLET_DE_JUGADOR_IDENTIFICADO);
        } else {
            // Defaults for new LotoWheel config
            this.currentSorteoConfig = null;
            // Sin snapshot al crear — el diff no aplica, se manda todo (POST).
            this._configSnapshotAlAbrir = null;
            // En nuevo sorteo: tab Acceso solo visible para superadmin (no hay nodo_id aún)
            this._maybeShowTabAcceso(null);
            // Nuevo sorteo: siempre se puede guardar
            const btnGuardar = document.getElementById('btnGuardarLotowheel');
            if (btnGuardar) {
                btnGuardar.disabled = false;
                btnGuardar.style.opacity = '';
                btnGuardar.title = '';
            }
            document.getElementById('lotowheelConfigNombre').value = '';
            document.getElementById('lotowheelConfigIntervalo').value = 60;
            document.getElementById('lotowheelConfigActivo').value = '1';
            // Tipo de Rueda default
            document.getElementById('lotowheelConfigTipoRuleta').value = TIPO_DE_RUEDA_INICIAL_PARA_FORM_ADMIN;
            this.onTipoRuletaChange();
            // Timing defaults
            document.getElementById('lotowheelConfigCloseBefore').value = 5;
            document.getElementById('lotowheelConfigWarningTime').value = 10;
            document.getElementById('lotowheelConfigMinUsable').value = 20;
            document.getElementById('lotowheelConfigSpinTime').value = 10;
            this.selectTheme('CASINO_ROYALE');
            document.getElementById('lotowheelConfigSoundPack').value = 'DEFAULT';
            document.getElementById('lotowheelConfigIdioma').value =
                (App.currentUser && App.currentUser.idioma) || 'es';
            document.getElementById('lotowheelConfigMonedaCodigo').value = MonedaService.ORDER[0];
            this.onMonedaChange();
            document.getElementById('lotowheelConfigDenominacion').value = 100;
            this.currentChips = [...DEFAULT_CHIPS];
            this.renderChipsList();
            document.getElementById('lotowheelConfigPremioMax').value = 360000;
            document.getElementById('lotowheelConfigSpinDuration').value = 10000;
            document.getElementById('lotowheelConfigSpinCorrection').value = 1000;
            document.getElementById('lotowheelConfigSpinRPM').value = 20;
            document.getElementById('lotowheelConfigSpinTension').value = 7;
            document.getElementById('lotowheelConfigSpinFM').value = 20;
            document.getElementById('lotowheelConfigPhaseAccel').value = 0.13;
            document.getElementById('lotowheelConfigPhaseCruise').value = 0.01;
            document.getElementById('lotowheelConfigPhaseDecel').value = 0.86;
            document.getElementById('lotowheelConfigEffectStart').value = 'orange';
            document.getElementById('lotowheelConfigEffectEnd').value = 'burst';
            document.getElementById('lotowheelConfigIdleMode').value = 'all';
            document.getElementById('lotowheelConfigIdleTimeout').value = 10;
            // Presentacion del cero — default POR_NUMERO
            this.populateZeroDisplayConfig(null, null);
            // Branding — sin assets en nuevo sorteo
            this._editingSorteoConfigId = null;
            this.populateBrandingPreviews(null, null);
            // Apuestas - todas habilitadas por defecto en AMBOS canales
            // (§8.14). El nuevo sorteo no restringe nada.
            _aplicarCheckboxesDeCanalParaGrupos(null, CANAL_DE_VENTA_TICKET_AL_PORTADOR);
            _aplicarCheckboxesDeCanalParaGrupos(null, CANAL_DE_VENTA_WALLET_DE_JUGADOR_IDENTIFICADO);

            // Resolver - defaults
            await this.loadResolverOptions();
            document.getElementById('resolverName').value = 'random';
            this.onResolverChange();
        }

        // Hidratación completa → habilitar botón Guardar según permisos.
        const btnGuardarFinal = document.getElementById('btnGuardarLotowheel');
        if (btnGuardarFinal) {
            const puede = this._puedeEditarModalActual;
            btnGuardarFinal.disabled = !puede;
            btnGuardarFinal.title = puede ? '' : t('Solo superadmin puede editar sorteos globales');
            btnGuardarFinal.style.opacity = puede ? '' : '0.4';
        }
        this._modalCargando = false;
    },

    /**
     * Etiquetas humanas de cada top-level key del config de un sorteo.
     * Usado por el toast de confirmación de guardado para que el admin
     * lea "Cambios en 'Loto 37': fichas, límites" en vez de un genérico
     * "guardado correctamente" — confirmación visual de que lo que tocó
     * se persistió y que NO se tocó lo que no debía.
     *
     * Cualquier key del diff que no esté acá cae al fallback (la key
     * cruda) — es defense-in-depth: si mañana se agrega un campo nuevo,
     * el toast lo muestra como "spin" o "ticketExpiryDays" en vez de
     * romperse o mentir.
     */
    _ETIQUETAS_HUMANAS_DE_CAMPOS_DEL_CONFIG_DE_SORTEO: {
        tipoRuleta: 'tipo de rueda',
        tema: 'tema visual',
        sound_pack: 'pack de sonidos',
        idioma: 'idioma',
        moneda: 'moneda',
        fichas: 'fichas',
        limites: 'límites',
        spin: 'animación de giro',
        effects: 'efectos',
        idle: 'comportamiento en espera',
        pozo: 'pozo acumulado',
        apuestasHabilitadas: 'apuestas habilitadas',
        resolver: 'algoritmo del resolver',
        close_before: 'cierre anticipado',
        warning_time: 'aviso previo al cierre',
        min_usable_time: 'tiempo mínimo útil',
        spin_time: 'duración del giro',
        zero_display_mode: 'presentación del cero',
        zero_display_labels: 'etiquetas del cero',
        ticketExpiryDays: 'vigencia de tickets',
        assets: 'branding visual',
    },

    /**
     * Construye el mensaje del toast preguntándole AL BORRADOR qué keys
     * cambiaron canónicamente. El modal solo aporta el vocabulario humano
     * de su entidad (sorteo lotowheel) — la lógica de comparación es
     * agnóstica y vive en el helper SSOT compartido.
     *
     * Si quisiéramos toast de confirmación en modales de nodos, pozos o
     * dispositivos, basta con: (1) crear su Borrador, (2) tener su tabla
     * de etiquetas humanas, (3) llamar al mismo método del Borrador.
     * Cero duplicación de lógica de comparación.
     */
    _construirMensajeDeConfirmacionDeGuardado(nombreDelSorteo, borrador, dataTopLevel) {
        const nombreParaMostrar = nombreDelSorteo || 'sorteo sin nombre';
        const keysCambiadas = borrador.calcularKeysQueRealmenteCambiaronEntreSnapshotYCopia();
        // Campos top-level (nombre/intervalo_segundos/activo) viven fuera del
        // JSON config: son columnas propias en DB. El borrador solo razona
        // sobre el config — sumamos acá los cambios top-level para que el
        // toast no diga "Sin cambios" cuando solo se editó uno de estos.
        const cambiosTopLevel = [];
        const original = this.currentSorteoConfig || {};
        if (dataTopLevel) {
            if (dataTopLevel.nombre !== undefined && dataTopLevel.nombre !== original.nombre) {
                cambiosTopLevel.push('nombre');
            }
            if (
                dataTopLevel.intervalo_segundos !== undefined &&
                dataTopLevel.intervalo_segundos !== original.intervalo_segundos
            ) {
                cambiosTopLevel.push('intervalo_segundos');
            }
            if (dataTopLevel.activo !== undefined && dataTopLevel.activo !== (original.activo ? 1 : 0)) {
                cambiosTopLevel.push('activo');
            }
        }
        if (keysCambiadas.length === 0 && cambiosTopLevel.length === 0) {
            return `${t('Sin cambios en')} "${nombreParaMostrar}"`;
        }
        const etiquetas = [...cambiosTopLevel, ...keysCambiadas].map(
            k =>
                this._ETIQUETAS_HUMANAS_DE_CAMPOS_DEL_CONFIG_DE_SORTEO[k] ||
                ETIQUETAS_HUMANAS_POR_PATH_DEL_SORTEO[k] ||
                k
        );
        return `${t('Cambios en')} "${nombreParaMostrar}": ${etiquetas.join(', ')}`;
    },

    async saveLotowheelConfig() {
        // Doble defensa: si llegó acá con el botón habilitado por bug
        // (event listener viejo, click programático desde devtools),
        // igual NO guardar mientras el modal esté en hidratación.
        if (this._modalCargando) return;

        const id = document.getElementById('lotowheelConfigId').value;

        // §8.14 — recolectar AMBOS canales (Ticket / Wallet). `pleno` siempre
        // queda incluida en la lista material (sin checkbox UI, ver
        // APUESTAS_SIEMPRE_HABILITADAS_SIN_CHECKBOX_UI). La normalización a
        // forma plana vs multi-canal se hace abajo con el helper SSOT.
        const enabledBetsTicket = _recolectarApuestasHabilitadasDelDOMParaCanal(CANAL_DE_VENTA_TICKET_AL_PORTADOR);
        const enabledBetsWallet = _recolectarApuestasHabilitadasDelDOMParaCanal(
            CANAL_DE_VENTA_WALLET_DE_JUGADOR_IDENTIFICADO
        );

        // ───────────────────────────────────────────────────────────────
        // CIRUGÍA INPUT-A-PATH (reemplaza al constructor desde scratch que
        // causó el bug de producción: editar chips borraba limites.maxApuesta
        // en 20/22 sorteos porque los campos no expuestos en el modal
        // quedaban fuera del objeto y el diff los marcaba como ausentes).
        //
        // CÓMO funciona ahora:
        //   1) Se crea un Borrador desde el snapshot completo del backend
        //      (en alta nueva, desde {} — todo se considera "agregado").
        //   2) Cada lectura del DOM escribe DIRECTO al path correspondiente
        //      del Borrador. NO se construye un objeto intermedio.
        //   3) Los campos que el modal NO expone permanecen IDÉNTICOS al
        //      snapshot → el diff no los incluye → backend NO los borra.
        //
        // Si mañana se agrega un input al modal y se olvida agregar su
        // línea acá, ese input NO HACE NADA (detectable en QA al instante)
        // pero NADA SE BORRA — el bug del modal viejo era estructuralmente
        // imposible de prevenir, este patrón lo hace estructuralmente
        // imposible de reproducir.
        // ───────────────────────────────────────────────────────────────
        const snapshotParaArrancarBorrador = id && this._configSnapshotAlAbrir ? this._configSnapshotAlAbrir : {};
        const borrador =
            BorradorDeEdicionDeEntidadConSnapshotOriginal.crearDesdeSnapshotOriginalRecibidoDelBackend(
                snapshotParaArrancarBorrador
            );

        // Tipo de Rueda
        borrador.escribirCampoEnPath('tipoRuleta', document.getElementById('lotowheelConfigTipoRuleta').value);

        // Timing
        borrador.escribirCampoEnPath(
            'close_before',
            parseInt(document.getElementById('lotowheelConfigCloseBefore').value)
        );
        borrador.escribirCampoEnPath(
            'warning_time',
            parseInt(document.getElementById('lotowheelConfigWarningTime').value)
        );
        borrador.escribirCampoEnPath(
            'min_usable_time',
            parseInt(document.getElementById('lotowheelConfigMinUsable').value)
        );
        borrador.escribirCampoEnPath('spin_time', parseInt(document.getElementById('lotowheelConfigSpinTime').value));

        // Visual
        borrador.escribirCampoEnPath(
            'tema',
            document.querySelector('.theme-option.selected')?.dataset.theme || 'CASINO_ROYALE'
        );
        borrador.escribirCampoEnPath('sound_pack', document.getElementById('lotowheelConfigSoundPack').value);
        borrador.escribirCampoEnPath('idioma', document.getElementById('lotowheelConfigIdioma').value);

        // Moneda
        borrador.escribirCampoEnPath('moneda.codigo', document.getElementById('lotowheelConfigMonedaCodigo').value);
        borrador.escribirCampoEnPath('moneda.simbolo', document.getElementById('lotowheelConfigMonedaSimbolo').value);
        borrador.escribirCampoEnPath(
            'moneda.decimales',
            parseInt(document.getElementById('lotowheelConfigMonedaDecimales').value)
        );
        borrador.escribirCampoEnPath(
            'moneda.separadorDecimal',
            document.getElementById('lotowheelConfigMonedaSepDec').value
        );
        borrador.escribirCampoEnPath(
            'moneda.separadorMiles',
            document.getElementById('lotowheelConfigMonedaSepMil').value
        );

        // Fichas
        borrador.escribirCampoEnPath(
            'fichas.denominacion',
            parseInt(document.getElementById('lotowheelConfigDenominacion').value)
        );
        borrador.escribirCampoEnPath('fichas.chips', this.currentChips);

        // Límites — el modal solo expone premioMaximo. Los otros (maxApuesta,
        // maxTotalTicket, maxDrawCount, plenoMultiplier) NO se tocan acá:
        // permanecen tal cual el snapshot del backend, intactos. Esto es
        // el corazón del fix.
        borrador.escribirCampoEnPath(
            'limites.premioMaximo',
            parseInt(document.getElementById('lotowheelConfigPremioMax').value)
        );

        // Vigencia de tickets
        borrador.escribirCampoEnPath(
            'ticketExpiryDays',
            parseInt(document.getElementById('lotowheelConfigTicketExpiryDays').value) || 180
        );

        // Spin (animación de rueda)
        borrador.escribirCampoEnPath(
            'spin.duration',
            parseInt(document.getElementById('lotowheelConfigSpinDuration').value)
        );
        borrador.escribirCampoEnPath(
            'spin.correctionDelay',
            parseInt(document.getElementById('lotowheelConfigSpinCorrection').value)
        );
        borrador.escribirCampoEnPath(
            'spin.cruiseRPM',
            parseInt(document.getElementById('lotowheelConfigSpinRPM').value)
        );
        borrador.escribirCampoEnPath(
            'spin.tensionFinal',
            parseInt(document.getElementById('lotowheelConfigSpinTension').value)
        );
        borrador.escribirCampoEnPath(
            'spin.fmQuesitos',
            parseInt(document.getElementById('lotowheelConfigSpinFM').value)
        );
        borrador.escribirCampoEnPath(
            'spin.phases.accel',
            parseFloat(document.getElementById('lotowheelConfigPhaseAccel').value)
        );
        borrador.escribirCampoEnPath(
            'spin.phases.cruise',
            parseFloat(document.getElementById('lotowheelConfigPhaseCruise').value)
        );
        borrador.escribirCampoEnPath(
            'spin.phases.decel',
            parseFloat(document.getElementById('lotowheelConfigPhaseDecel').value)
        );

        // Effects
        borrador.escribirCampoEnPath('effects.start', document.getElementById('lotowheelConfigEffectStart').value);
        borrador.escribirCampoEnPath('effects.end', document.getElementById('lotowheelConfigEffectEnd').value);

        // Idle
        borrador.escribirCampoEnPath('idle.mode', document.getElementById('lotowheelConfigIdleMode').value);
        borrador.escribirCampoEnPath(
            'idle.timeout',
            parseInt(document.getElementById('lotowheelConfigIdleTimeout').value)
        );

        // Presentación del cero
        borrador.escribirCampoEnPath(
            'zero_display_mode',
            this.collectZeroDisplayMode() !== 'POR_NUMERO' ? this.collectZeroDisplayMode() : null
        );
        borrador.escribirCampoEnPath('zero_display_labels', this.collectZeroDisplayLabels());

        // Apuestas habilitadas POR CANAL (§8.14) — solo si la UI puede
        // representar todas las apuestas del tipo de rueda actual. Si el
        // usuario abre un sorteo cuya rueda no soporta ciertas apuestas
        // (ej. cuarteto en mini18), esos checkbox no existen en el DOM. El
        // contrato declara con `aplicaSi` cuáles aplican, pero la UI hoy
        // renderiza por BET_GROUPS — si no están todos, evitamos pisar el
        // snapshot con un array incompleto. Cuando migremos también la
        // pestaña Apuestas a renderizado declarativo desde el contrato,
        // este guard se vuelve innecesario.
        //
        // Política de persistencia (helper SSOT canal-de-venta.js):
        //   - Ambos canales = todo on → escribe `apuestasHabilitadas: null`
        //     + `apuestasHabilitadasPorCanalDeVenta: null` (sin restricción).
        //   - Ambos canales con MISMA lista → forma plana legacy
        //     `apuestasHabilitadas: [ids]` + multi-canal en null.
        //   - Canales con listas DISTINTAS → forma multi-canal
        //     `apuestasHabilitadasPorCanalDeVenta: { ticket, wallet }` +
        //     plana en null. Cero estados de dos verdades coexistiendo.
        const TODOS_LOS_GRUPOS_TIENEN_AMBOS_CHECKBOXES_EN_EL_DOM = Object.keys(BET_GROUPS).every(
            groupId =>
                document.getElementById(`${groupId}_ticket`) !== null &&
                document.getElementById(`${groupId}_wallet`) !== null
        );
        if (TODOS_LOS_GRUPOS_TIENEN_AMBOS_CHECKBOXES_EN_EL_DOM) {
            const { apuestasHabilitadas, apuestasHabilitadasPorCanalDeVenta } =
                normalizarApuestasHabilitadasParaPersistencia(enabledBetsTicket, enabledBetsWallet);
            borrador.escribirCampoEnPath('apuestasHabilitadas', apuestasHabilitadas);
            borrador.escribirCampoEnPath('apuestasHabilitadasPorCanalDeVenta', apuestasHabilitadasPorCanalDeVenta);
        }

        // Resolver — solo escribimos si la cache cargó (loadResolverOptions
        // resolvió) y el dropdown tiene valor. Si NO, preservamos el snapshot
        // (que viene hidratado con `{name:'random', config:{}}` por defecto
        // del contrato cuando es nuevo, o con el resolver real del sorteo).
        // Esto NO es defensa contra race del usuario tapeando rápido — es
        // defensa contra el caso real de "cache no cargada" (offline, error
        // de red), donde collectResolverConfigFromForm devolvería
        // `{name:'', config:{}}` y pisaría el resolver válido del snapshot.
        const resolverCacheCargada = Array.isArray(this._resolverCache) && this._resolverCache.length > 0;
        if (resolverCacheCargada && document.getElementById('resolverName')?.value) {
            borrador.escribirCampoEnPath('resolver', this.collectResolverConfigFromForm());
        }

        // Branding assets: NO se tocan acá. Como la copia de trabajo arrancó
        // del deepClone del snapshot, los assets viejos ya viven adentro y
        // se enviarán intactos al backend. Si nunca existieron, no aparecen.
        // Cero acción == cero riesgo de pisarlos.

        const data = {
            nombre: document.getElementById('lotowheelConfigNombre').value,
            intervalo_segundos: parseInt(document.getElementById('lotowheelConfigIntervalo').value),
            activo: document.getElementById('lotowheelConfigActivo').value === '1' ? 1 : 0,
        };

        // Serie de tickets — override por sorteo (columna aparte, no dentro de config)
        if (App.currentUser && App.currentUser.rol === 'superadmin') {
            data.serie_tickets_overrides = this._collectSerieTicketsOverridesFromForm();
        }

        // Validación contra contrato canónico ANTES del envío. Mismo
        // contrato que valida el backend — defense in depth + UX (mensaje
        // de error inmediato sin esperar el round-trip).
        const erroresContrato = borrador.validarContraContrato(CONTRATO_SORTEO_LOTOWHEEL);
        if (erroresContrato.length > 0) {
            const e = erroresContrato[0];
            const etiqueta = ETIQUETAS_HUMANAS_POR_PATH_DEL_SORTEO[e.path] || e.path;
            App.toast(`Error en "${etiqueta}": ${motivoErrorAHumano(e.motivo)}`, 'error');
            return;
        }

        // ENVÍO al backend — política Carmack/SSOT: siempre mandamos el objeto
        // completo de la copia de trabajo. El backend hace deepMerge sobre lo
        // que tiene en DB, así que mandar el objeto completo es semánticamente
        // idéntico a mandar un diff mínimo, pero ELIMINA toda una categoría de
        // bugs:
        //   - "diff fantasma" cuando una función collector arma un sub-objeto
        //     ligeramente distinto al snapshot por basura histórica de schemas
        //     viejos (caso real: resolver con 36 nulls heredados que generaban
        //     "Cambios en X: algoritmo del resolver" sin que nadie tocara nada).
        //   - Pérdida de campos que el modal no expone (ya cubierto por la
        //     cirugía input-a-path, pero el objeto completo lo refuerza).
        //
        // Costo: ~2KB por save vs ~500B. Despreciable. Beneficio: forense
        // completo en audit log + UI honesta + cero categoría de bugs de diff.
        //
        // Tanto en alta (sin id) como en edición (con id) se envía igual.
        const copiaDeTrabajoCompleta = borrador.exportarCopiaDeTrabajoCompleta();
        data.config = JSON.stringify(copiaDeTrabajoCompleta);

        let result;
        if (id) {
            // Verificar permiso antes de enviar: sorteo global solo editable por superadmin
            const esSorteoGlobal = this.currentSorteoConfig && !this.currentSorteoConfig.nodo_id;
            if (esSorteoGlobal && App.currentUser.rol !== ROL_ADMIN.SUPERADMIN) {
                App.toast(t('Solo superadmin puede editar sorteos globales'), 'error');
                return;
            }
            result = await Api.updateLotowheelConfig(id, data);
        } else {
            // Al crear: los no-superadmin crean sorteos propios (nodo_id = su nodo)
            if (App.currentUser.rol !== ROL_ADMIN.SUPERADMIN) {
                data.nodo_id = App.currentUser.nodo_id;
            }
            result = await Api.createLotowheelConfig(data);
        }

        if (result) {
            // Al editar, persistir también pozos y accesos (tienen endpoints separados)
            if (id) {
                const suscripciones = [];
                for (let i = 0; i < this._pozosComunesDisponibles.length; i++) {
                    const p = this._pozosComunesDisponibles[i];
                    const check = document.getElementById(`pozo_check_${p.id}`);
                    if (check && check.checked) {
                        const pctInput = document.getElementById(`pozo_pct_${p.id}`);
                        const paralizarInput = document.getElementById(`pozo_paralizar_${p.id}`);
                        const pctVal = pctInput?.value.trim();
                        suscripciones.push({
                            pozo_id: p.id,
                            porcentaje_aporte: pctVal !== '' ? parseFloat(pctVal) || null : null,
                            paralizar_segundos: paralizarInput ? parseInt(paralizarInput.value) || 0 : 0,
                        });
                    }
                }
                // Si todos los nodos están marcados = abierto a todos → guardar lista vacía en BD
                const todosSeleccionados =
                    this._accesoNodos.length > 0 && this._accesoNodoIdsSeleccionados.size >= this._accesoNodos.length;
                const nodoIds = todosSeleccionados ? [] : Array.from(this._accesoNodoIdsSeleccionados);

                const parallelCalls = [];

                // Solo PUT si el panel fue cargado — sin este guard, guardar
                // el modal sin abrir el tab Acceso vaciaría sorteo_accesos.
                if (this._accesoNodos.length > 0) {
                    parallelCalls.push(Api.callAdmin(`/admin/sorteos/${id}/accesos`, 'PUT', { nodo_ids: nodoIds }));
                }

                // Solo actualizar suscripciones a pozos si el panel fue cargado (evita borrar
                // suscripciones existentes cuando el usuario guarda sin haber visitado el tab Pozo)
                if (this._pozosComunesDisponibles.length > 0) {
                    parallelCalls.push(Api.callAdmin(`/admin/sorteos/${id}/pozos-comunes`, 'PUT', { suscripciones }));
                }
                if (parallelCalls.length > 0) {
                    await Promise.all(parallelCalls);
                }
            }
            // Si el backend nos devuelve info de cuándo se aplicará al scheduler,
            // mostrarlo al operador con detalle (mensaje listo desde el backend).
            // Estados posibles: SCHEDULER_APLICACION del SSOT compartido.
            const mensajeBase = this._construirMensajeDeConfirmacionDeGuardado(data.nombre, borrador, data);
            const mensajeFinal = this._mensajeFinalSegunAplicacionScheduler(mensajeBase, result);
            App.toast(
                mensajeFinal,
                result.aplicacionScheduler === SCHEDULER_APLICACION.DIFERIDO ? 'warning' : 'success'
            );
            App.closeModal();
            this.loadLotowheelConfigs();
        }
    },

    _mensajeFinalSegunAplicacionScheduler(mensajeBase, result) {
        if (!result || !result.aplicacionScheduler) return mensajeBase;
        if (result.aplicacionScheduler === SCHEDULER_APLICACION.RECARGADO_INMEDIATO) {
            return `${mensajeBase} ${t('· Aplicado al scheduler.')}`;
        }
        if (result.aplicacionScheduler === SCHEDULER_APLICACION.DIFERIDO && result.message_operador) {
            // El backend ya armó un mensaje legible con hora local + motivo.
            return `${mensajeBase}\n\n${result.message_operador}`;
        }
        return mensajeBase;
    },

    async deleteLotowheelConfig() {
        // Guard contra doble ejecución (CLAUDE.md §1.9): el flujo es two-step
        // async (callAdminConfirmable → confirm() → callAdmin forzado). Sin
        // este flag, doble-tap en el botón Eliminar dispara dos primeras
        // requests paralelas — la segunda puede pegarle a un sorteo ya borrado.
        if (this._deleteEnProgreso) return;
        this._deleteEnProgreso = true;
        try {
            const id = document.getElementById('lotowheelConfigId').value;
            if (!id) return;

            const nombre = document.getElementById('lotowheelConfigNombre').value || 'esta configuracion';
            if (
                !confirm(
                    `${t('¿Eliminar "')}${nombre}" ${t('y todos sus resultados?')}\n\n${t('Esta accion no se puede deshacer.')}`
                )
            ) {
                return;
            }

            // Primer intento sin forzar — el backend bloquea con 409 si hay bearer
            // multi-sorteo pendientes (jugadas pagadas todavía no jugadas).
            const result = await Api.callAdminConfirmable(`/lotowheel/configs/${id}`, 'DELETE');
            if (result === null) return;

            // 409 con codigo BEARER_MULTI_SORTEO_PENDIENTES: mostrar advertencia
            // explícita y pedir confirmación adicional. Si el operador acepta,
            // reintenta con ?confirmar=destruccion (asume reembolso manual).
            if (result.ok === false && result.codigo === SCHEDULER_CODIGO_ERROR.BEARER_MULTI_SORTEO_PENDIENTES) {
                const tickets = Array.isArray(result.tickets) ? result.tickets : [];
                const codigosLista = tickets
                    .slice(0, TICKETS_MAX_CONFIRM_DIALOG)
                    .map(t => `#${t.codigo}`)
                    .join(', ');
                const sufijo =
                    tickets.length > TICKETS_MAX_CONFIRM_DIALOG
                        ? ` ${t('y')} ${tickets.length - TICKETS_MAX_CONFIRM_DIALOG} ${t('más')}`
                        : '';
                const detalle = codigosLista ? `\n\n${t('Tickets afectados:')} ${codigosLista}${sufijo}.` : '';
                const aceptar = confirm(
                    `${t('ATENCIÓN —')} ${result.error}.${detalle}\n\n` +
                        `${t('Si elimina ahora los clientes PERDERÁN las jugadas restantes ya pagadas. Debe gestionar el reembolso manual.')}\n\n${t('¿Continuar con la eliminación FORZADA?')}`
                );
                if (!aceptar) {
                    App.toast(t('Eliminación cancelada. El sorteo sigue activo.'), 'info');
                    return;
                }
                const forzado = await Api.callAdmin(
                    `/lotowheel/configs/${id}?confirmar=${SCHEDULER_CONFIRMAR_DESTRUCCION}`,
                    'DELETE'
                );
                if (forzado === null) return;
                if (forzado.ok === false) {
                    App.toast(
                        `${forzado.error || 'No se pudo eliminar'} — ${forzado.accion_sugerida || ''}`.trim(),
                        'error'
                    );
                    return;
                }
                App.toast(forzado.message || t('LotoWheel eliminado (forzado con bearer huérfanos)'), 'warning');
                App.closeModal();
                this.loadLotowheelConfigs();
                return;
            }

            // Otros 502 (scheduler no se pudo detener, error DB, etc.).
            if (result.ok === false) {
                App.toast(`${result.error || 'No se pudo eliminar'} — ${result.accion_sugerida || ''}`.trim(), 'error');
                return;
            }
            App.toast(result.message || t('LotoWheel eliminado'));
            App.closeModal();
            this.loadLotowheelConfigs();
        } finally {
            this._deleteEnProgreso = false;
        }
    },

    // ── Series de tickets: override por sorteo ──────────────────────
    _seriesTicketsDisponibles: [],

    async _populateSeriesTicketsDropdowns(sorteoConfig) {
        const section = document.getElementById('lotowheelSeriesTicketsSection');
        if (!section) return;

        // Solo visible para superadmin
        if (!App.currentUser || App.currentUser.rol !== 'superadmin') {
            section.style.display = 'none';
            return;
        }
        section.style.display = 'block';

        // Cargar series activas del backend
        try {
            const data = await App.fetchWithAuth('/api/admin/series-tickets');
            this._seriesTicketsDisponibles = data?.series || [];
        } catch (_err) {
            this._seriesTicketsDisponibles = [];
        }

        const selectIds = ['lotowheelConfigSerieBearer', 'lotowheelConfigSerieCuenta', 'lotowheelConfigSerieVlt'];
        const canalKeys = ['bearer', 'cuenta', 'vlt'];

        // Parsear overrides actuales del sorteo
        let overrides = null;
        if (sorteoConfig && sorteoConfig.serie_tickets_overrides) {
            try {
                overrides =
                    typeof sorteoConfig.serie_tickets_overrides === 'string'
                        ? JSON.parse(sorteoConfig.serie_tickets_overrides)
                        : sorteoConfig.serie_tickets_overrides;
            } catch (_e) {
                overrides = null;
            }
        }

        for (let i = 0; i < selectIds.length; i++) {
            const select = document.getElementById(selectIds[i]);
            if (!select) continue;

            // Limpiar y poblar opciones
            select.innerHTML = `<option value="">${t('Heredar')}</option>`;
            for (let j = 0; j < this._seriesTicketsDisponibles.length; j++) {
                const serie = this._seriesTicketsDisponibles[j];
                if (!serie.activa) continue;
                const option = document.createElement('option');
                option.value = String(serie.id);
                option.textContent = serie.nombre + (serie.es_default_global ? ` ${t('(global)')}` : '');
                select.appendChild(option);
            }

            // Seleccionar el valor actual si hay override
            const valorActual = overrides ? overrides[canalKeys[i]] : null;
            select.value = valorActual ? String(valorActual) : '';
        }

        // Renderizar las filas de partners actuales (si las hay)
        const partnersBloque =
            overrides && overrides.partners && typeof overrides.partners === 'object' ? overrides.partners : null;
        this._renderPartnerOverrideSerieRows(partnersBloque);
    },

    // ─────────────────────────────────────────────────────────────────
    // Partners inline — filas dinámicas para override por partner_code.
    // Cada fila es (input text con partner_code) + (select con series)
    // + (botón eliminar). No hay catálogo global de partner_codes: el
    // admin los tipea libremente. El backend valida la forma
    // [A-Za-z0-9_-]{1,64} al guardar (validar-serie-tickets-overrides-bloque).
    // ─────────────────────────────────────────────────────────────────

    _renderPartnerOverrideSerieRows(partnersBloqueActual) {
        const contenedorListaPartners = document.getElementById('lotowheelConfigSeriePartnersList');
        if (!contenedorListaPartners) return;

        contenedorListaPartners.innerHTML = '';
        if (!partnersBloqueActual) return;

        const partnerCodes = Object.keys(partnersBloqueActual);
        for (let i = 0; i < partnerCodes.length; i++) {
            const partnerCode = partnerCodes[i];
            const serieIdAsignada = partnersBloqueActual[partnerCode];
            const filaDom = this._crearFilaDomParaPartnerOverride(partnerCode, serieIdAsignada);
            contenedorListaPartners.appendChild(filaDom);
        }
    },

    _crearFilaDomParaPartnerOverride(partnerCodePrellenado, serieIdAsignadaPrellenada) {
        const filaWrapper = document.createElement('div');
        filaWrapper.className = 'form-row lotowheel-partner-override-row';
        filaWrapper.style.gap = '8px';
        filaWrapper.style.alignItems = 'flex-end';
        filaWrapper.style.marginBottom = '8px';

        // Columna: partner_code (input text)
        const columnaPartnerCode = document.createElement('div');
        columnaPartnerCode.className = 'form-group';
        columnaPartnerCode.style.flex = '1';
        const inputPartnerCode = document.createElement('input');
        inputPartnerCode.type = 'text';
        inputPartnerCode.className = 'form-input js-partner-override-code';
        inputPartnerCode.placeholder = 'partner_code (ej: CASINO_A)';
        inputPartnerCode.maxLength = 64;
        if (partnerCodePrellenado) inputPartnerCode.value = partnerCodePrellenado;
        columnaPartnerCode.appendChild(inputPartnerCode);
        filaWrapper.appendChild(columnaPartnerCode);

        // Columna: select de serie
        const columnaSerieSelect = document.createElement('div');
        columnaSerieSelect.className = 'form-group';
        columnaSerieSelect.style.flex = '1';
        const selectSerie = document.createElement('select');
        selectSerie.className = 'form-input js-partner-override-serie';
        const optHeredar = document.createElement('option');
        optHeredar.value = '';
        optHeredar.textContent = t('Heredar');
        selectSerie.appendChild(optHeredar);
        const seriesActivasDisponibles = this._seriesTicketsDisponibles || [];
        for (let i = 0; i < seriesActivasDisponibles.length; i++) {
            const serie = seriesActivasDisponibles[i];
            if (!serie.activa) continue;
            const opt = document.createElement('option');
            opt.value = String(serie.id);
            opt.textContent = serie.nombre + (serie.es_default_global ? ` ${t('(global)')}` : '');
            selectSerie.appendChild(opt);
        }
        if (serieIdAsignadaPrellenada) {
            selectSerie.value = String(serieIdAsignadaPrellenada);
        }
        columnaSerieSelect.appendChild(selectSerie);
        filaWrapper.appendChild(columnaSerieSelect);

        // Columna: boton eliminar
        const columnaBoton = document.createElement('div');
        columnaBoton.style.flex = '0 0 auto';
        const botonEliminar = document.createElement('button');
        botonEliminar.type = 'button';
        botonEliminar.className = 'btn btn-secondary btn-sm';
        botonEliminar.textContent = '×';
        botonEliminar.title = 'Quitar esta fila';
        botonEliminar.addEventListener('click', function () {
            filaWrapper.parentNode && filaWrapper.parentNode.removeChild(filaWrapper);
        });
        columnaBoton.appendChild(botonEliminar);
        filaWrapper.appendChild(columnaBoton);

        return filaWrapper;
    },

    agregarPartnerOverrideSerie() {
        const contenedor = document.getElementById('lotowheelConfigSeriePartnersList');
        if (!contenedor) return;
        contenedor.appendChild(this._crearFilaDomParaPartnerOverride('', null));
    },

    _collectSerieTicketsOverridesFromForm() {
        const selectIds = ['lotowheelConfigSerieBearer', 'lotowheelConfigSerieCuenta', 'lotowheelConfigSerieVlt'];
        const canalKeys = ['bearer', 'cuenta', 'vlt'];

        const overrides = {};
        let tieneAlgo = false;

        for (let i = 0; i < selectIds.length; i++) {
            const select = document.getElementById(selectIds[i]);
            if (!select) continue;
            const val = select.value;
            if (val && val !== '') {
                overrides[canalKeys[i]] = parseInt(val, 10);
                tieneAlgo = true;
            }
        }

        // Partners: iterar las filas dinamicas y armar el bloque partners.
        // Las filas con partner_code vacio o con serie 'Heredar' se omiten
        // — equivale a no configurar override para ese partner.
        const contenedorPartners = document.getElementById('lotowheelConfigSeriePartnersList');
        if (contenedorPartners) {
            const filas = contenedorPartners.querySelectorAll('.lotowheel-partner-override-row');
            const bloquePartners = {};
            let partnersConAlgo = false;
            for (let i = 0; i < filas.length; i++) {
                const fila = filas[i];
                const inputCode = fila.querySelector('.js-partner-override-code');
                const selectSerie = fila.querySelector('.js-partner-override-serie');
                if (!inputCode || !selectSerie) continue;
                const partnerCodeNormalizado = (inputCode.value || '').trim();
                const valorSerieSeleccionado = selectSerie.value;
                if (!partnerCodeNormalizado || !valorSerieSeleccionado) continue;
                bloquePartners[partnerCodeNormalizado] = parseInt(valorSerieSeleccionado, 10);
                partnersConAlgo = true;
            }
            if (partnersConAlgo) {
                overrides.partners = bloquePartners;
                tieneAlgo = true;
            }
        }

        // Si no hay ningún override, mandar null (= heredar todo del operador)
        return tieneAlgo ? overrides : null;
    },
};

// ---- SECCIONES EXTRAÍDAS A ARCHIVOS SEPARADOS ----
// lotowheel-modal-apariencia.js  → renderThemeGrid, selectTheme, onMonedaChange, onTipoRuletaChange, updateMultipliers, updateBetDescriptions
// lotowheel-modal-fichas.js      → generateChipLabel, renderChipsList, validatePremioMaximo, updateChip*, addChip, removeChip
// lotowheel-modal-bloqueos.js    → loadBloqueos, openBloqueoModal, saveBloqueo, deleteBloqueo
// lotowheel-modal-algoritmo.js   → loadResolverOptions, _renderPresetCards, _showPresetDropdown, onResolverChange, loadPreset, saveCurrentAsPreset, sliders, simulateProfile
// lotowheel-modal-acceso.js      → _maybeShowTabAcceso, loadAccesoPanel, _renderAccesoEstadoBadge, _renderAccesoNodoList, onAccesoNodoToggle, saveAccesoNodos
// lotowheel-modal-pozos.js       → loadPozoComunesPanel, _renderPozoComunesPanel, _onPozoComunToggle, saveSuscripcionesPozosComunes

// Placeholder para que no quede código muerto — los métodos reales
// están en los archivos de modal listados arriba.
void 0; // lotowheel-modal-apariencia.js
